import store from '@/store';
import { requireAuthenticatedSession } from '@/common/auth';
import TimeDiff from '../common/getTimeDiff';
import { handleApiResponse } from '@/common/serviceResponse';
import { isTransientNetworkError, performRequest } from '@/common/network';

const STATUS_UPDATE_RETRY_DELAYS_MS = [300, 1000];
const SUBMIT_RETRY_DELAYS_MS = [1000, 3000];

function wait(ms) {
    return new Promise((resolve) => {
        window.setTimeout(resolve, ms);
    });
}

export default {
    async getAssessments() {
        const token = await requireAuthenticatedSession();
        const url = process.env.VUE_APP_APIURL + '/api/v1/assessments/' + TimeDiff.getTimeDiff() + '?platform=desktop';
        const response = await performRequest(url, {
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        }, {
            service: 'AssessmentService.getAssessments',
            trainer: store.getters.getIsTrainer,
        });
        return handleApiResponse(response);
    },
    async getAssessment(assessmentID) {
        const token = await requireAuthenticatedSession();
        const url = process.env.VUE_APP_APIURL + '/api/v1/assessment/' + assessmentID;
        const response = await performRequest(url, {
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        }, {
            service: 'AssessmentService.getAssessment',
            assessmentId: assessmentID,
        });
        return handleApiResponse(response);
    },
    async updateStatus(assessmentID, status, failureReason, additionalInfo = {}) {
        const token = await requireAuthenticatedSession();
        const url = process.env.VUE_APP_APIURL + '/api/v1/assessment/' + assessmentID + '/update-status';
        let info = {
            'app': 'desktop',
            'version': process.env.VUE_APP_VERSION,
            'ai': store.getters.getAI === true && store.getters.getSecureAssessment === true,
            ...additionalInfo,
        };
        if (failureReason){
            info.failureReason = failureReason;
        }

        const payload = {
            'status': status,
            'additionalInfo': info
        };
        let response;

        for (let attempt = 0; attempt <= STATUS_UPDATE_RETRY_DELAYS_MS.length; attempt++) {
            try {
                response = await performRequest(url, {
                    method: 'POST',
                    keepalive: true,
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(payload)
                }, {
                    service: 'AssessmentService.updateStatus',
                    assessmentId: assessmentID,
                    payload,
                    attempt: attempt + 1,
                });
                break;
            } catch (error) {
                const shouldRetry = isTransientNetworkError(error) && attempt < STATUS_UPDATE_RETRY_DELAYS_MS.length;

                if (!shouldRetry) {
                    throw error;
                }

                await wait(STATUS_UPDATE_RETRY_DELAYS_MS[attempt]);
            }
        }

        return handleApiResponse(response, {
            expectSuccess: true,
        });
    },
    async submitAssessment(assessmentID, formData) {
        const token = await requireAuthenticatedSession();
        const url = process.env.VUE_APP_APIURL + '/api/v1/assessment/' + assessmentID + '/submit';
        let response;

        for (let attempt = 0; attempt <= SUBMIT_RETRY_DELAYS_MS.length; attempt++) {
            try {
                response = await performRequest(url, {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: formData
                }, {
                    service: 'AssessmentService.submitAssessment',
                    assessmentId: assessmentID,
                    attempt: attempt + 1,
                });
                break;
            } catch (error) {
                const shouldRetry = isTransientNetworkError(error) && attempt < SUBMIT_RETRY_DELAYS_MS.length;

                if (!shouldRetry) {
                    throw error;
                }

                await wait(SUBMIT_RETRY_DELAYS_MS[attempt]);
            }
        }

        return handleApiResponse(response, {
            expectSuccess: true,
        });
    },
    async submitFlag(assessmentID, reason) {
        const token = await requireAuthenticatedSession();
        const url = process.env.VUE_APP_APIURL + '/api/v1/assessment/' + assessmentID + '/flag?reason=' + reason;
        const response = await performRequest(url, {
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        }, {
            service: 'AssessmentService.submitFlag',
            assessmentId: assessmentID,
            reason,
        });
        return handleApiResponse(response, {
            expectSuccess: true,
        });
    }
}
